/** Two taps within this window count as one right-click. */
export const DOUBLE_TAP_MS = 350;
/** Second tap must land this close to the first (px). */
export const DOUBLE_TAP_PX = 28;
/** Movement larger than this is a drag, not a tap. */
export const TAP_MOVE_PX = 16;

export type DoubleTapPoint = { x: number; y: number; t: number };
export type DoubleTapState = DoubleTapPoint & { moved: boolean };

const SKIP_SELECTOR =
  "input, textarea, select, option, button, a, label, [role='button'], [type='submit'], [contenteditable='true'], [data-no-double-tap-menu]";

export function isTouchLikePointer(event: { pointerType: string }): boolean {
  return event.pointerType === "touch";
}

export function shouldSkipDoubleTap(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true;
  return Boolean(target.closest(SKIP_SELECTOR));
}

export function isSecondTap(
  last: DoubleTapState | null,
  next: DoubleTapPoint,
  opts?: { maxMs?: number; maxPx?: number },
): boolean {
  if (!last || last.moved) return false;
  const maxMs = opts?.maxMs ?? DOUBLE_TAP_MS;
  const maxPx = opts?.maxPx ?? DOUBLE_TAP_PX;
  return next.t - last.t <= maxMs && Math.hypot(next.x - last.x, next.y - last.y) <= maxPx;
}

/** Pixel offset inside `target` — R3F raycasts with offsetX/offsetY, not clientX. */
export function contextMenuOffsets(
  target: Element,
  point: { clientX: number; clientY: number },
): { offsetX: number; offsetY: number } {
  const rect = target.getBoundingClientRect();
  return {
    offsetX: point.clientX - rect.left,
    offsetY: point.clientY - rect.top,
  };
}

/** Prefer the WebGL canvas under the tap so the outer wall layer is the hit. */
export function resolveContextMenuTarget(target: EventTarget | null, clientX: number, clientY: number): EventTarget {
  if (typeof document !== "undefined" && typeof document.elementsFromPoint === "function") {
    const canvas = document.elementsFromPoint(clientX, clientY).find((el) => el instanceof HTMLCanvasElement);
    if (canvas) return canvas;
  }
  return target ?? document;
}

export function dispatchSyntheticContextMenu(target: EventTarget, event: PointerEvent): boolean {
  const dispatchOn = resolveContextMenuTarget(target, event.clientX, event.clientY);
  const el = dispatchOn instanceof Element ? dispatchOn : null;
  const offsets = el ? contextMenuOffsets(el, event) : { offsetX: 0, offsetY: 0 };
  const ctx = new MouseEvent("contextmenu", {
    bubbles: true,
    cancelable: true,
    composed: true,
    view: window,
    clientX: event.clientX,
    clientY: event.clientY,
    screenX: event.screenX,
    screenY: event.screenY,
    button: 2,
    buttons: 2,
    ctrlKey: event.ctrlKey,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    metaKey: event.metaKey,
  });
  Object.defineProperties(ctx, {
    offsetX: { configurable: true, get: () => offsets.offsetX },
    offsetY: { configurable: true, get: () => offsets.offsetY },
    layerX: { configurable: true, get: () => offsets.offsetX },
    layerY: { configurable: true, get: () => offsets.offsetY },
  });
  return dispatchOn.dispatchEvent(ctx);
}

/** On a phone, a second tap in place fires the same `contextmenu` as a right mouse click. */
export function attachDoubleTapContextMenu(root: EventTarget = document): () => void {
  let last: DoubleTapState | null = null;
  let down: { x: number; y: number; pointerId: number } | null = null;

  const onPointerDown = (event: Event) => {
    const e = event as PointerEvent;
    if (!isTouchLikePointer(e)) return;
    if (e.isPrimary === false) return;
    if (typeof e.button === "number" && e.button !== 0) return;
    if (shouldSkipDoubleTap(e.target)) {
      last = null;
      down = null;
      return;
    }
    const next = { x: e.clientX, y: e.clientY, t: e.timeStamp };
    if (isSecondTap(last, next)) {
      last = null;
      down = null;
      e.preventDefault();
      e.stopPropagation();
      dispatchSyntheticContextMenu(e.target ?? root, e);
      return;
    }
    down = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    last = { x: e.clientX, y: e.clientY, t: e.timeStamp, moved: false };
  };

  const onPointerMove = (event: Event) => {
    const e = event as PointerEvent;
    if (!last || !down || e.pointerId !== down.pointerId) return;
    if (Math.hypot(e.clientX - down.x, e.clientY - down.y) > TAP_MOVE_PX) {
      last.moved = true;
    }
  };

  const onPointerUp = (event: Event) => {
    const e = event as PointerEvent;
    if (!last || !down || e.pointerId !== down.pointerId) return;
    if (last.moved) last = null;
    down = null;
  };

  const onPointerCancel = () => {
    last = null;
    down = null;
  };

  root.addEventListener("pointerdown", onPointerDown, true);
  root.addEventListener("pointermove", onPointerMove, true);
  root.addEventListener("pointerup", onPointerUp, true);
  root.addEventListener("pointercancel", onPointerCancel, true);
  return () => {
    root.removeEventListener("pointerdown", onPointerDown, true);
    root.removeEventListener("pointermove", onPointerMove, true);
    root.removeEventListener("pointerup", onPointerUp, true);
    root.removeEventListener("pointercancel", onPointerCancel, true);
  };
}
